import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { adminApi, ApiError, type Invitation } from './admin-api';

/**
 * The admin invitations console.
 *
 * Renders an invite form (email + role) over a table of live pending
 * invitations with a per-row Revoke action. Every mutation goes through
 * {@link adminApi} (Bearer-authed, server-enforced) and refreshes the list on
 * success. Server `{ error }` messages are surfaced verbatim — inline under the
 * invite form for invite failures (403 non-Ricoh / 409 already a user / 400),
 * and in the list banner for load / revoke failures.
 *
 * Light-DOM (no shadow root) so the page picks up admin.css + the Paper tokens
 * loaded by index.html, exactly like {@link import('./admin-users-page')}.
 */
@customElement('admin-invitations-page')
export class AdminInvitationsPage extends LitElement {
  /** Current pending invitations (newest first). */
  @state() private invitations: Invitation[] = [];

  @state() private loading = true;
  /** List-level error (banner) — load + revoke failures. */
  @state() private error: string | null = null;

  /** Invite form in-flight + its inline error / success notice. */
  @state() private inviteBusy = false;
  @state() private inviteError: string | null = null;
  @state() private inviteNotice: string | null = null;

  /** email of the invitation whose revoke is currently in flight. */
  @state() private busyEmail: string | null = null;

  /** Confirm-revoke dialog target (null = closed). */
  @state() private confirmRevoke: Invitation | null = null;
  @state() private revokeBusy = false;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  // ─── Data ──────────────────────────────────────────────────────────────
  private async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const res = await adminApi.listInvitations();
      this.invitations = res.invitations ?? [];
    } catch (err) {
      this.error = errMessage(err);
      this.invitations = [];
    } finally {
      this.loading = false;
    }
  }

  // ─── Invite ──────────────────────────────────────────────────────────────
  private onInviteSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    if (this.inviteBusy) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const email = String(data.get('email') ?? '').trim();
    const role = (String(data.get('role') ?? 'member') === 'admin' ? 'admin' : 'member') as
      | 'admin'
      | 'member';
    if (!email) {
      this.inviteError = 'An email address is required.';
      this.inviteNotice = null;
      return;
    }
    this.inviteBusy = true;
    this.inviteError = null;
    this.inviteNotice = null;
    try {
      await adminApi.inviteUser(email, role);
      form.reset();
      this.inviteNotice = `Invitation sent to ${email}.`;
      await this.load();
    } catch (err) {
      // 403 non-Ricoh · 409 already a user · 400 bad/missing → server message.
      this.inviteError = errMessage(err);
    } finally {
      this.inviteBusy = false;
    }
  };

  // ─── Revoke (confirm dialog) ───────────────────────────────────────────────
  private askRevoke = (inv: Invitation): void => {
    this.confirmRevoke = inv;
  };
  private cancelRevoke = (): void => {
    if (this.revokeBusy) return;
    this.confirmRevoke = null;
  };
  private confirmRevokeNow = async (): Promise<void> => {
    const inv = this.confirmRevoke;
    if (!inv || this.revokeBusy) return;
    this.revokeBusy = true;
    this.busyEmail = inv.email;
    this.error = null;
    try {
      await adminApi.revokeInvitation(inv.email);
      this.confirmRevoke = null;
      await this.load();
    } catch (err) {
      this.error = errMessage(err);
    } finally {
      this.revokeBusy = false;
      this.busyEmail = null;
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  render(): TemplateResult {
    return html`
      <div class="page admin-page">
        <div class="page-head">
          <div class="titles">
            <h1>Invitations</h1>
            <p class="page-sub">Invite people to Workplace by Ricoh and manage pending invites.</p>
          </div>
        </div>

        ${this.renderInviteForm()}
        ${this.error ? html`<p class="admin-banner" role="alert">${this.error}</p>` : nothing}
        ${this.renderBody()} ${this.renderRevokeDialog()}
      </div>
    `;
  }

  private renderInviteForm(): TemplateResult {
    return html`
      <form class="invite-form" @submit=${this.onInviteSubmit} novalidate>
        <div class="field invite-email">
          <label for="inv-email">Email</label>
          <input
            id="inv-email"
            class="input"
            name="email"
            type="email"
            autocomplete="off"
            placeholder="person@ricoh-europe.com"
            ?disabled=${this.inviteBusy}
            required
          />
        </div>
        <div class="field invite-role">
          <label for="inv-role">Role</label>
          <select id="inv-role" class="select" name="role" ?disabled=${this.inviteBusy}>
            <option value="member" selected>Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div class="invite-submit">
          <button class="btn-primary" type="submit" ?disabled=${this.inviteBusy}>
            ${this.inviteBusy ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
      ${this.inviteError ? html`<p class="err" role="alert">${this.inviteError}</p>` : nothing}
      ${this.inviteNotice
        ? html`<p class="invite-notice" role="status">${this.inviteNotice}</p>`
        : nothing}
    `;
  }

  private renderBody(): TemplateResult {
    if (this.loading && this.invitations.length === 0) {
      return html`<div class="state" role="status">Loading invitations…</div>`;
    }
    if (!this.loading && this.invitations.length === 0) {
      return html`<div class="state">No pending invitations.</div>`;
    }
    return html`
      <div class="admin-table-wrap" aria-busy=${this.loading ? 'true' : 'false'}>
        <table class="admin-table">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Invited by</th>
              <th scope="col">Invited</th>
              <th scope="col">Expires</th>
              <th scope="col" class="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${this.invitations.map((inv) => this.renderRow(inv))}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderRow(inv: Invitation): TemplateResult {
    const disabled = this.busyEmail === inv.email || !!this.busyEmail;
    return html`
      <tr>
        <td>
          <div class="user-meta">
            <span class="user-email">${inv.email}</span>
          </div>
        </td>
        <td>
          <span class="badge ${inv.role === 'admin' ? 'badge-admin' : 'badge-member'}"
            >${inv.role}</span
          >
        </td>
        <td class="num">${inv.invitedBy || html`<span class="muted">—</span>`}</td>
        <td class="num">${relTime(inv.createdAt)}</td>
        <td class="num">${expiresLabel(inv.expiresAt)}</td>
        <td class="col-actions">
          <div class="row-actions">
            <button
              class="cell-action danger"
              type="button"
              ?disabled=${disabled}
              @click=${() => this.askRevoke(inv)}
            >
              Revoke
            </button>
          </div>
        </td>
      </tr>
    `;
  }

  private renderRevokeDialog(): TemplateResult | typeof nothing {
    const inv = this.confirmRevoke;
    if (!inv) return nothing;
    return html`
      <div
        class="admin-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.cancelRevoke();
        }}
      >
        <div
          class="admin-modal small"
          role="dialog"
          aria-modal="true"
          aria-labelledby="revoke-title"
        >
          <div class="modal-head">
            <h2 id="revoke-title">Revoke invitation</h2>
          </div>
          <p class="modal-text">
            Revoke the invitation for <strong>${inv.email}</strong>? They will no longer be able to
            use it to sign up.
          </p>
          ${this.error ? html`<p class="err" role="alert">${this.error}</p>` : nothing}
          <div class="modal-actions">
            <button
              class="btn-secondary"
              type="button"
              ?disabled=${this.revokeBusy}
              @click=${this.cancelRevoke}
            >
              Cancel
            </button>
            <button
              class="btn-primary danger-btn"
              type="button"
              ?disabled=${this.revokeBusy}
              @click=${this.confirmRevokeNow}
            >
              ${this.revokeBusy ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

// ─── helpers ───────────────────────────────────────────────────────────────

/** Pull a user-facing message off an unknown thrown value. */
function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

/** Absolute date (e.g. "2 Jun 2026") for fallbacks. Accepts an RFC3339 string. */
function dateOnly(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

/** Humanised past relative time (e.g. "3 days ago") for the Invited column.
 *  Falls back to an absolute date for anything older than ~30 days. Accepts an
 *  RFC3339 string. */
function relTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return dateOnly(iso);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  return dateOnly(iso);
}

/** Humanised expiry (e.g. "in 7 days", "in 3 hours", "Expired") for the Expires
 *  column. Falls back to an absolute date beyond ~30 days. Accepts an RFC3339
 *  string. */
function expiresLabel(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  const diff = ms - Date.now();
  if (diff <= 0) return 'Expired';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'in under a minute';
  const min = Math.floor(sec / 60);
  if (min < 60) return `in ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `in ${hr} hour${hr === 1 ? '' : 's'}`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `in ${day} day${day === 1 ? '' : 's'}`;
  return dateOnly(iso);
}

declare global {
  interface HTMLElementTagNameMap {
    'admin-invitations-page': AdminInvitationsPage;
  }
}
